import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {AutomatedEmailTable} from 'shells/automated-email-table/automated-email-table';

import type {EmailAutomationField} from 'nutshell-graphql-types';
import type {
    GetEmailAutomationsQueued as GetEmailAutomationsQueuedQuery,
    GetEmailAutomationsQueuedVariables,
    EmailAutomationStatusStatus as EmailAutomationStatus,
    DelayQueuedEmail as DelayQueuedEmailMutation,
    DelayQueuedEmailVariables as DelayQueuedEmailMutationVariables,
    SendNowQueuedEmail as SendNowQueuedEmailMutation,
    SendNowQueuedEmailVariables as SendNowQueuedEmailMutationVariables,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import DELAY_QUEUED_EMAIL from '../mailbox/graphql/mutations/delay-queued-email.graphql';
import SEND_NOW_QUEUED_EMAIL from '../mailbox/graphql/mutations/send-now-queued-email.graphql';

import {AutomationListWrapper} from './automation-list-wrapper';
import {convertEdgesToTableData} from './convert-edges-to-table-data';
import GET_QUEUED from './graphql/queries/get-email-automations-queued.graphql';

const LIMIT = 25;
const VALID_STATUSES: EmailAutomationStatus[] = ['QUEUED_AUTOMATICALLY', 'QUEUED_MANUALLY'];
const STATUS = 'QUEUED';

type GraphQLProps = {
    onCancel: any;
    sendNow: any;
    delay: any;
    data: any;
    loading: any;
    fetchMore: any;
    error: any;
    onUpdateStatusField: (
        emailAutomationStatus: any,
        field: EmailAutomationField,
        newValue?: string | null | undefined
    ) => Promise<any>;
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

type Props = ContextRouter & GraphQLProps;

function AutomationsSendingComponent(props: Props) {
    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_AUTOMATIONS_SENDING_VIEWED);
    }, []);

    const {onCancel, sendNow, delay, data, loading, fetchMore, error, onUpdateStatusField} = props;

    const {endCursor, hasNextPage} =
        data && data.emailAutomationStatusesQueued
            ? data.emailAutomationStatusesQueued.pageInfo
            : {};

    const edges =
        data && data.emailAutomationStatusesQueued ? data.emailAutomationStatusesQueued.edges : [];
    const tableData = convertEdgesToTableData(
        edges.filter(
            // @ts-expect-error
            (edge) =>
                VALID_STATUSES.includes(edge.node.status) &&
                // Remove emails that have already been sent. Kinda hacky, but
                // this should only happen if the user manually sends an email
                // from this page. We need to remove the row from the page
                edge.node.emailSequence &&
                edge.node.emailSequence.emailsQueued.length &&
                !edge.node.emailSequence.emailsQueued[0].isAlreadySent
        )
    );

    return (
        <AutomatedEmailTable
            status={STATUS}
            data={tableData}
            isLoading={loading}
            errMessage={safelyGetGraphQLErrorMessage(error)}
            hasNextPage={hasNextPage}
            history={props.history}
            hasAutomatedEmailSequences={props.hasAutomatedEmailSequences}
            hasConfiguredAutomations={props.hasConfiguredAutomations}
            loadMore={() =>
                fetchMore({
                    variables: {
                        cursor: endCursor,
                        limit: LIMIT,
                    },
                    // @ts-expect-error
                    updateQuery: (prev, {fetchMoreResult}) => {
                        if (!fetchMoreResult) return prev;
                        const prevData = prev.emailAutomationStatusesQueued;
                        const newData = fetchMoreResult.emailAutomationStatusesQueued;

                        return {
                            ...prev,
                            emailAutomationStatusesQueued: {
                                ...prevData,
                                pageInfo: newData.pageInfo,
                                edges: [...prevData.edges, ...newData.edges],
                            },
                        };
                    },
                })
            }
            onUpdateField={(emailAutomationStatusId, field, newValue) => {
                const emailAutomationStatus = edges.find(
                    // @ts-expect-error
                    (edge) => edge.node.id === emailAutomationStatusId
                );

                return onUpdateStatusField(emailAutomationStatus, field, newValue);
            }}
            onEmailAutomationStatusCancel={(emailAutomationStatusId) => {
                const currentEmailAutomationStatus = edges.find(
                    // @ts-expect-error
                    (edge) => edge.node.id === emailAutomationStatusId
                );

                return onCancel(emailAutomationStatusId, currentEmailAutomationStatus);
            }}
            onDelayOneBusinessDay={(queuedEmailId) => {
                return delay({
                    variables: {
                        payload: {
                            queuedEmail: {
                                id: queuedEmailId,
                                delay: {
                                    unit: 'bzd',
                                    timespan: 1,
                                },
                            },
                        },
                    },
                });
            }}
            onDelayOneWeek={(queuedEmailId) => {
                return delay({
                    variables: {
                        payload: {
                            queuedEmail: {
                                id: queuedEmailId,
                                delay: {
                                    unit: 'ww',
                                    timespan: 1,
                                },
                            },
                        },
                    },
                });
            }}
            onSendNow={(queuedEmailId) => {
                analyticsClient.track(
                    analyticsClient.EVENTS.TRACK_AUTOMATION_STATUS_SENT_NOW_FROM_LIST
                );

                return sendNow({
                    variables: {
                        payload: {
                            queuedEmail: {
                                id: queuedEmailId,
                            },
                        },
                    },
                });
            }}
        />
    );
}

class QueuedQuery extends Query<
    GetEmailAutomationsQueuedQuery,
    GetEmailAutomationsQueuedVariables
> {}
class SendNowMutation extends Mutation<
    SendNowQueuedEmailMutation,
    SendNowQueuedEmailMutationVariables
> {}
class DelayMutation extends Mutation<DelayQueuedEmailMutation, DelayQueuedEmailMutationVariables> {}

type AutomationsSendingComponentGraphQLProps = ContextRouter & {
    onCancel: (id: string, currentEmailAutomationStatus?: any) => Promise<any>;
    onUpdateStatusField: (
        emailAutomationStatus: any,
        field: EmailAutomationField,
        newValue?: string | null | undefined
    ) => Promise<any>;
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

/**
 * This annoying layer just gets the render props from AutomationListWrapper so that
 * we can use them as `props` in the actual component to do things like set up hooks and effects.
 */
function AutomationsSendingPlusCancelAndModified(props: ContextRouter) {
    return (
        <AutomationListWrapper
            query={GET_QUEUED}
            fetchLimit={LIMIT}
            validStatuses={VALID_STATUSES}
            cacheKey='emailAutomationStatusesQueued'
        >
            {(wrapperProps) => <AutomationsSendingComponentGraphQL {...props} {...wrapperProps} />}
        </AutomationListWrapper>
    );
}

function AutomationsSendingComponentGraphQL(props: AutomationsSendingComponentGraphQLProps) {
    const {onCancel, onUpdateStatusField} = props;

    return (
        <SendNowMutation mutation={SEND_NOW_QUEUED_EMAIL} refetchQueries={['GetEmailStats']}>
            {(sendNow) => (
                <DelayMutation mutation={DELAY_QUEUED_EMAIL}>
                    {(delay) => (
                        <QueuedQuery
                            query={GET_QUEUED}
                            variables={{limit: LIMIT}}
                            notifyOnNetworkStatusChange={true}
                            // @ts-expect-error
                            fetchPolicy='cache-and-network'
                        >
                            {({data, loading, fetchMore, error}) => (
                                <AutomationsSendingComponent
                                    {...props}
                                    onCancel={onCancel}
                                    sendNow={sendNow}
                                    delay={delay}
                                    data={data}
                                    loading={loading}
                                    fetchMore={fetchMore}
                                    error={error}
                                    onUpdateStatusField={onUpdateStatusField}
                                />
                            )}
                        </QueuedQuery>
                    )}
                </DelayMutation>
            )}
        </SendNowMutation>
    );
}

// @ts-expect-error
export const AutomationsSending = withRouter<Record<any, any>>(
    AutomationsSendingPlusCancelAndModified
);
